import { FilterDescriptor } from '@progress/kendo-data-query'
import { FieldSettings, TextFilter } from '@progress/kendo-react-data-tools'
import { TransactionFilterKey } from 'api/Transaction/Types/Transaction'
import { useAppSelector } from 'store'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'

import { BooleanFilter } from './BooleanFilter'
import { CountryFilter, CustomDateFilter } from './common.filter'
import { SelectFilter } from './SelectFilter'
import { PaymentProviderFilter } from './TransactionsFields'

const CompaniesFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const data = tickmillCompanies.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <SelectFilter
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

const TransactionTypeFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const transactionTypes = useAppSelector((state) => state.transactions.types)

  const data = transactionTypes.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <SelectFilter
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 280 }}
    />
  )
}

const TransactionStatus = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const transactionStatus = useAppSelector((state) => state.transactions.status)

  const data = transactionStatus.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const getDepositWithdrawalFields = (): FieldSettings[] => [
  {
    name: TransactionFilterKey.reference,
    label: 'Reference',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.likeOperator,
        operator: GridFilterOperatorEnum.likeOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.groupReference,
    label: 'Group Reference',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.likeOperator,
        operator: GridFilterOperatorEnum.likeOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.type,
    label: 'Type',
    filter: TransactionTypeFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.amount,
    label: 'Amount',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.walletTransactionPaymentProviderTransaction,
    label: 'Payment Provider',
    filter: PaymentProviderFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.state,
    label: 'Status',
    filter: TransactionStatus,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.walletTransactionWalletCompany,
    label: 'Company',
    filter: CompaniesFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.countryId,
    label: 'Country',
    filter: CountryFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.createdDate,
    label: 'Date Created',
    filter: CustomDateFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOperator,
        operator: GridFilterOperatorEnum.afterOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOrEqualOperator,
        operator: GridFilterOperatorEnum.afterOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
        operator: GridFilterOperatorEnum.beforeOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOperator,
        operator: GridFilterOperatorEnum.beforeOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.updatedDate,
    label: 'Date Updated',
    filter: CustomDateFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOperator,
        operator: GridFilterOperatorEnum.afterOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOrEqualOperator,
        operator: GridFilterOperatorEnum.afterOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
        operator: GridFilterOperatorEnum.beforeOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOperator,
        operator: GridFilterOperatorEnum.beforeOperator,
      },
    ],
  },
  {
    name: TransactionFilterKey.isInternalTestAccount,
    label: 'Is Test Client',
    filter: BooleanFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
]
